import React from 'react';
import { Box } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/use-auth';
import { findActiveSection, getVisibleSections } from '../../router/navigation';
import MobileTopBar from './MobileTopBar';
import MobileSectionTabs from './MobileSectionTabs';
import MobileBottomNav, { MORE_TAB } from './MobileBottomNav';
import MobileMoreSheet from './MobileMoreSheet';
import { MOBILE_MAX_PRIMARY_TABS } from './constants';

export default function MobileAppShell({ headerTitle, children }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const scrollRef = React.useRef(null);
  const [moreOpen, setMoreOpen] = React.useState(false);

  const role = auth?.user?.role;
  const sections = React.useMemo(() => getVisibleSections(role), [role]);
  const primarySections = sections.slice(0, MOBILE_MAX_PRIMARY_TABS);
  const activeSection = findActiveSection(sections, pathname);
  const bottomNavValue = primarySections.some((section) => section.key === activeSection?.key)
    ? activeSection.key
    : MORE_TAB;

  React.useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [pathname]);

  const goTo = (path) => {
    setMoreOpen(false);
    if (path === pathname) {
      scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    navigate(path);
  };

  const handleBottomNavChange = (value) => {
    if (value === MORE_TAB) {
      setMoreOpen(true);
      return;
    }
    const section = sections.find((item) => item.key === value);
    if (section) {
      goTo(section.key === activeSection?.key ? pathname : section.path);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',
        width: '100%',
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}
    >
      <MobileTopBar
        title={headerTitle}
        sectionTitle={activeSection?.children.length > 1 ? activeSection.title : null}
        userName={auth?.user?.name}
        onOpenAccount={() => setMoreOpen(true)}
      />
      <MobileSectionTabs section={activeSection} pathname={pathname} onNavigate={goTo} />
      <Box
        component="main"
        ref={scrollRef}
        id="mobile-scroll-container"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          overscrollBehaviorY: 'contain',
          px: 1.5,
          pt: 1.5,
          pb: 12,
        }}
      >
        {children}
      </Box>
      <MobileBottomNav
        sections={primarySections}
        value={bottomNavValue}
        onChange={handleBottomNavChange}
        showMore
      />
      <MobileMoreSheet
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        sections={sections}
        pathname={pathname}
        user={auth?.user}
        onNavigate={goTo}
        onSignOut={() => {
          setMoreOpen(false);
          auth.signOut();
          navigate('/login');
        }}
      />
    </Box>
  );
}
